// =============================================================================
// components/KpiIncidents/IncidentKpiDistribution.jsx
//
// Cards horizontais informativos — um por KPI com incidentes.
// Cada card mostra:
//   [Tech badge] [KPI name]  [Open count]  [mini proportion bar]
//
// Click filtra por esse KPI (re-click remove).
// =============================================================================
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import { TECH_COLOR, SEVERITY_OPTIONS } from '../../constants/kpiConstants';

const SEV_MAP = SEVERITY_OPTIONS.reduce(function (acc, s) {
	acc[s.value] = s;
	return acc;
}, {});

function KpiCard({ entry, isSelected, onClick }) {
	const techColor = TECH_COLOR[entry.tech] || '#94a3b8';
	const sev       = SEV_MAP[entry.severity] || SEV_MAP[3];

	const openPct   = entry.total > 0 ? (entry.open / entry.total) * 100 : 0;

	return (
		<Tooltip
			title={
				<Box sx={{ fontSize: '0.78rem', p: 0.5 }}>
					<div><strong>{entry.kpiName}</strong></div>
					<div>{entry.open} abertos · {entry.closed} fechados</div>
					<div style={{ marginTop: 4, fontStyle: 'italic', opacity: 0.85 }}>
						Click para filtrar
					</div>
				</Box>
			}
			placement="top"
			arrow
		>
			<Box
				onClick={onClick}
				sx={{
					px: 1.5, py: 1,
					minWidth: 200,
					maxWidth: 280,
					cursor: 'pointer',
					borderRadius: 1,
					border: '1px solid',
					borderColor: isSelected ? techColor : '#e5e7eb',
					bgcolor: isSelected ? techColor + '0d' : 'background.paper',
					transition: 'all 0.15s ease',
					'&:hover': {
						borderColor: techColor,
						bgcolor: techColor + '08',
					},
				}}
			>
				{/* Top row: tech badge + KPI name + severity dot */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
					<Box sx={{
						px: 0.75, py: 0.125,
						bgcolor: techColor,
						color: 'white',
						fontSize: '0.65rem',
						fontWeight: 700,
						borderRadius: 0.5,
						lineHeight: 1.5,
						minWidth: 24,
						textAlign: 'center',
					}}>
						{entry.tech}
					</Box>
					<Typography variant="body2" sx={{
						fontSize: '0.78rem', fontWeight: 500,
						flex: 1, minWidth: 0,
						overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
					}}>
						{entry.kpiName}
					</Typography>
					<Box sx={{
						width: 5, height: 5, borderRadius: '50%', bgcolor: sev.color,
					}} />
				</Box>

				{/* Bottom row: number + proportion bar */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Typography sx={{
						fontSize: '1.15rem', fontWeight: 700,
						color: '#dc2626', lineHeight: 1, fontVariantNumeric: 'tabular-nums',
					}}>
						{entry.open.toLocaleString('pt-PT')}
					</Typography>
					<Box sx={{ flex: 1 }}>
						<Box sx={{
							height: 4, bgcolor: '#f1f5f9', borderRadius: 2,
							overflow: 'hidden', position: 'relative',
						}}>
							<Box sx={{
								position: 'absolute', left: 0, top: 0, height: '100%',
								width: openPct + '%',
								bgcolor: '#dc2626',
								borderRadius: 2,
							}} />
						</Box>
						<Typography variant="caption" sx={{
							display: 'block', mt: 0.25, fontSize: '0.65rem', color: 'text.secondary',
							lineHeight: 1.2,
						}}>
							{Math.round(openPct)}% abertos · {entry.total.toLocaleString('pt-PT')} total
						</Typography>
					</Box>
				</Box>
			</Box>
		</Tooltip>
	);
}

export default function IncidentKpiDistribution({ distribution, selectedKpiName, onSelectKpi }) {
	if (!distribution || distribution.length === 0) {
		return null;
	}

	const totalOpen = distribution.reduce(function (a, e) { return a + e.open; }, 0);

	return (
		<Paper variant="outlined" sx={{ p: 1.5 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
				<StackedBarChartIcon fontSize="small" sx={{ color: 'text.secondary' }} />
				<Typography variant="subtitle2" fontWeight={700}>
					Distribuicao por KPI
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
					{distribution.length} KPI{distribution.length === 1 ? '' : 's'} ·
					{' '}
					<Box component="span" sx={{ color: '#dc2626', fontWeight: 600 }}>
						{totalOpen.toLocaleString('pt-PT')}
					</Box>
					{' '}abertos
				</Typography>
			</Box>

			<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
				{distribution.map(function (entry) {
					return (
						<KpiCard
							key={entry.key}
							entry={entry}
							isSelected={selectedKpiName === entry.kpiName}
							onClick={function () {
								onSelectKpi(selectedKpiName === entry.kpiName ? 'all' : entry.kpiName);
							}}
						/>
					);
				})}
			</Box>
		</Paper>
	);
}
